import { useState, CSSProperties, ReactNode } from 'react';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import TableLayout from '../TableLayout';
import GrayBox from '../common/GrayBox';
import { Stack } from 'components/Stack';
import { Text } from 'components/Typography';
import { Button } from 'components/Button';

import {
  CalendarIconContainer,
  CalendarInputField,
  QueueSelectorContainer,
  QueueSelectorWrapper,
} from 'pages/Report/utils/ReportFilter';
import { ReportFilterContent } from 'pages/Report/utils/ReportFilterContent';
import ComplexDoubleviewDateselector from 'components/ComplexDoubleviewDateselector';
import Selector from 'components/Selector';
import ManualTableComponent from './ManualTableComponent';
import { ReactComponent as IcDateFill } from 'constants/icon/ic_date_fill.svg';
import { ReactComponent as IcArrowDropDown } from 'constants/icon/ic_arrow_dropdown.svg';
import { ReactComponent as IcRemove } from 'constants/icon/input_remove.svg';
import { ReactComponent as IcMagnifyingGlass } from 'constants/icon/ic_magnifyingglass.svg';
import { TRequestFilter } from './Manual';
import { useCheckedCustomerInfos } from './ManualStore';
import { getOrderList } from 'api';
import { dtoMessageManualList } from '../dto';
import { useTransferRecipent } from '../History/HistoryStore';
import { IMultiSkillData } from 'types/masterData/multiSkill';

export const OptionalFilterList = [
  { key: '고객명', value: 'consigneeName', suffix: '을' },
  { key: '주문 ID', value: 'orderCode', suffix: '를' },
  { key: '담당 기사님', value: 'driverName', suffix: '을' },
  { key: '아이템 명', value: 'productName', suffix: '을' },
];

export interface IManualMessage {
  order_id: string; // 주문ID - orderId
  order_status: string; // 주문상태 -
  customer_name: string; // 고객명
  customer_phone: string; // 고객연락처
  driver_name: string; // 담당기사님
  company_order_id: string; // 업체주문번호
  order_date: string; // 주문접수일
  address: string; // 주소 - address
  detail_address: string; // 상세주소
  item_name: string; // 아이템명
  item_count: number; // 아이템수량
  // 추가 설정 부분
  desiredDate?: string; // 작업희망일
  actualServiceTime?: number; // 실제 작업 소요 시간 (분)
  capacity?: number; // 예상 용적량
  completedAt?: string; // 작업 완료 일시
  consigneeNote?: string; // 고객전달사항
  desiredTimeEnd?: string; // 희망 시간(이전)
  desiredTimeStart?: string; // 희망 시간(이후)
  priority?: string; // 우선순위
  serviceTime?: number; // 예상 작업 소요 시간 (분)
  serviceVehicle?: string; // 담당 드러이버 지정
  shipmentType?: string; // 주문 유형
  skills?: IMultiSkillData[]; // 특수 조건
  note1?: string; // 비고1
  note2?: string; // 비고2
  note3?: string; // 비고3
  note4?: string; // 비고4
  note5?: string; // 비고5
}

const fetchManualTable = async (params: TRequestFilter) => {
  let requestParams: {
    orderStatus: string;
    receivedDate: string;
    routeStatus: string;
    priority: string;
    searchItem?: string;
    keyword?: string;
  } = {
    orderStatus: params.orderStatus,
    receivedDate: dayjs(params.startDate).format('YYYYMMDD') + '-' + dayjs(params.endDate).format('YYYYMMDD'),
    routeStatus: 'activated,processing,completed,none', // 고정
    priority: 'high,medium,low', // 고정
  };
  if (!!params.keyword && params.keyword.length > 0) {
    requestParams = {
      ...requestParams,
      searchItem: params.searchItem,
      keyword: params.keyword,
    };
  }
  const { orderList } = await getOrderList(requestParams);
  //@ts-expect-error
  return dtoMessageManualList(orderList);
};

type FormValues = {
  startDate?: string;
  endDate?: string;
  searchItem: typeof OptionalFilterList[number]['key'];
  keyword?: string;
};

type Props = {
  requestFilter: TRequestFilter;
  setRequestFilter: React.Dispatch<React.SetStateAction<TRequestFilter>>;
  sx?: CSSProperties;
  closeButton?: React.ReactNode;
};

const ManualTable = ({ requestFilter, setRequestFilter, sx, closeButton }: Props) => {
  const { checkedCustomerInfos, setCheckedCustomerInfos } = useCheckedCustomerInfos();
  const { transferRecipent } = useTransferRecipent();

  const [orderStatusFilter, setOrderStatusFilter] = useState([
    { key: '전체', value: 'all', isSelected: true },
    { key: '미배차', value: 'unassigned', isSelected: false },
    { key: '배차완료', value: 'scheduled', isSelected: false },
    { key: '처리중', value: 'processing,arrived', isSelected: false },
    { key: '처리완료', value: 'completed', isSelected: false },
    { key: '보류', value: 'skipped', isSelected: false },
    { key: '취소', value: 'deleted', isSelected: false },
  ]);

  const [CDDIsOpen, setCDDIsOpen] = useState<boolean>(false);
  const [placeHolderText, setPlaceHolderText] = useState(`${OptionalFilterList[0].key}${OptionalFilterList[0].suffix}`);

  const methods_default_Values: FormValues = {
    startDate: dayjs().subtract(6, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    keyword: '',
    searchItem: OptionalFilterList[0].value,
  };
  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    shouldFocusError: true,
    defaultValues: methods_default_Values,
  });

  const onClickSearch = () => {
    // const orderStatus = orderStatusFilter.find(item => item.isSelected)?.value;
    const orderStatus = orderStatusFilter
      .reduce((acc, cur, index) => {
        if (cur.isSelected) {
          return acc + ',' + cur.value;
        } else {
          return acc;
        }
      }, '')
      .substring(1);

    const { startDate, endDate, searchItem, keyword } = methods.getValues();
    setRequestFilter({
      orderStatus: orderStatus ?? 'all',
      startDate: startDate ?? '',
      endDate: endDate ?? '',
      searchItem: searchItem,
      keyword: keyword ?? '',
    });
    // 조회 시 이전에 선택한 고객 초기화 처리
    setCheckedCustomerInfos([]);
  };

  const {
    isFetching,
    data: manualMessageList,
    refetch: manualMessageListRefetch,
  } = useQuery(['manualMessageList', requestFilter], () => fetchManualTable(requestFilter), {
    onSuccess: res => {
      if (!!transferRecipent && transferRecipent.length > 0) {
        // 발성이력 조회에서 넘어온 수취인 + 기존꺼 중복 제거 후 넣어주기
        const filteredCustomerInfos = [...checkedCustomerInfos, ...transferRecipent].reduce((acc, cur) => {
          const findIndex = acc.findIndex(item => item.customer_phone === cur.customer_phone);
          if (findIndex === -1) {
            return [...acc, cur];
          }
          return acc;
        }, checkedCustomerInfos);
        setCheckedCustomerInfos(filteredCustomerInfos);
      }
    },
    enabled: !!requestFilter,
  });

  return (
    <TableLayout
      title="메시지 수동 발송"
      desc="주문별 고객에게 문자 메시지를 발송하거나 예약할 수 있습니다."
      sx={sx}
      closeButton={closeButton}
    >
      <Stack spacing={40}>
        <GrayBox
          sx={{
            borderRadius: '0px 12px 12px',
            flexDirection: 'row',
            padding: '12px 16px',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Stack
            name="sub-filter-wrapper"
            sx={{
              margin: '20px 0 0 0',
              minHeight: 'fit-content',
              maxHeight: '270px',
              transition: 'all .6s',
            }}
            style={{ alignItems: 'start', gap: 6 }}
          >
            <ReportFilterContent
              title="주문 상태"
              altInfo={`주문 상태 필터입니다.`}
              anchor="bottom"
              direction="row"
              gap={20}
            >
              <QueueSelectorWrapper direction={'row'} sx={{ width: '448px', marginLeft: '22px' }}>
                {orderStatusFilter.map((d, index) => {
                  return (
                    <QueueSelectorContainer
                      key={d.key}
                      selected={d.isSelected}
                      onClick={() => {
                        setOrderStatusFilter(
                          orderStatusFilter.map((item, idx) => {
                            const countIsSelected = orderStatusFilter.filter(item => item.isSelected).length;
                            const unSelected = orderStatusFilter.filter(item => !item.isSelected);
                            if (d.key === '전체') {
                              if (d.key === item.key) {
                                return { ...item, isSelected: true };
                              } else {
                                return { ...item, isSelected: false };
                              }
                            } else {
                              if (countIsSelected === 5 && unSelected.some(u => u.key === d.key)) {
                                if (item.key === '전체') {
                                  return { ...item, isSelected: true };
                                } else {
                                  return { ...item, isSelected: false };
                                }
                              } else {
                                if (item.key === '전체') {
                                  return { ...item, isSelected: false };
                                } else {
                                  if (d.key === item.key) {
                                    if (countIsSelected === 1 && d.isSelected) {
                                      return { ...item };
                                    }
                                    return { ...item, isSelected: !d.isSelected };
                                  } else {
                                    return { ...item };
                                  }
                                }
                              }
                            }

                            // if (d.key === item.key) {
                            //   if (countIsSelected === 1 && d.isSelected) {
                            //     return { ...item };
                            //   }
                            //   return { ...item, isSelected: !d.isSelected };
                            // } else {
                            //   return { ...item };
                            // }

                            // return { ...item, isSelected: index === idx };
                          })
                        );
                      }}
                      len={7}
                    >
                      <Text styleName={'caption2'} color={d.isSelected ? 'RC02' : 'RG02'}>
                        {d.key}
                      </Text>
                    </QueueSelectorContainer>
                  );
                })}
              </QueueSelectorWrapper>
            </ReportFilterContent>
            <SecondFilterDiv>
              <SecondFilterInnerDiv>
                <SecondFilterInnerTextDiv>주문 접수일</SecondFilterInnerTextDiv>
                <ComplexDoubleviewDateselector
                  inputReadOnly
                  parent={
                    <SecondFilterInnerInputDiv isActive={CDDIsOpen} hasError={false}>
                      <CalanderIconContainer isActive={CDDIsOpen} hasError={false}>
                        <IcDateFill />
                      </CalanderIconContainer>
                      <input
                        readOnly
                        {...methods.register('startDate', {
                          minLength: {
                            value: 10,
                            message: '',
                          },
                          maxLength: {
                            value: 10,
                            message: '',
                          },
                          pattern: {
                            value: /^\d{4}.(0[1-9]|1[012]).(0[1-9]|[12][0-9]|3[01])$/,
                            message: '',
                          },
                        })}
                        autoComplete="off"
                        onFocus={e => setCDDIsOpen(true)}
                      />
                      {'~'}
                      <input
                        readOnly
                        {...methods.register('endDate', {
                          minLength: {
                            value: 10,
                            message: '',
                          },
                          maxLength: {
                            value: 10,
                            message: '',
                          },
                          pattern: {
                            value: /^\d{4}.(0[1-9]|1[012]).(0[1-9]|[12][0-9]|3[01])$/,
                            message: '',
                          },
                        })}
                        autoComplete="off"
                        onFocus={e => setCDDIsOpen(true)}
                      />
                    </SecondFilterInnerInputDiv>
                  }
                  {...{
                    initDate: [
                      dayjs(methods.getValues('startDate')).toDate(),
                      dayjs(methods.getValues('endDate')).toDate(),
                    ],
                    methods,
                    isOpen: CDDIsOpen,
                    setIsOpen: setCDDIsOpen,
                  }}
                />
              </SecondFilterInnerDiv>
              <SecondFilterInnerDiv width={360}>
                <Selector
                  defaultValue={methods.getValues('searchItem') ?? OptionalFilterList[0].value}
                  options={OptionalFilterList}
                  onOptionsClick={item => {
                    methods.setValue('searchItem', item.value, {});
                    //@ts-expect-error
                    setPlaceHolderText(`${item.key}${item.suffix}`);
                  }}
                  icon={<IcArrowDropDown />}
                  sx={{ width: '110px', height: '30px', border: 'none', color: '#25282F' }}
                />

                <SearchbarInputContainer
                  spacing={4}
                  direction="row"
                  justify="space-between"
                  isFilled={!!(methods.watch('keyword') && methods.watch('keyword') !== '')}
                >
                  <IcMagnifyingGlass
                    className="spyglass"
                    width={16}
                    height={16}
                    onClick={() => {
                      //need to add form action
                    }}
                  />
                  <input
                    {...methods.register('keyword', {})}
                    autoComplete="off"
                    placeholder={`${placeHolderText} 입력해주세요`}
                    style={{
                      textDecorationColor: 'rgb(86, 91, 105)',
                      letterSpacing: '-0.02em',
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: 20,
                      whiteSpace: 'break-spaces !important',
                    }}
                  />
                  {(methods.watch('keyword')?.length ?? 0) > 0 && (
                    <IcRemove
                      tabIndex={-1}
                      className="removeBtn"
                      width={16}
                      height={16}
                      onClick={() => {
                        methods.resetField('keyword');
                      }}
                    />
                  )}
                </SearchbarInputContainer>
              </SecondFilterInnerDiv>
            </SecondFilterDiv>
          </Stack>
          <Stack style={{ alignItems: 'end' }}>
            <Button type="button" variant="secondary" h={32} onClick={onClickSearch}>
              조회하기
            </Button>
          </Stack>
        </GrayBox>
      </Stack>
      <Stack style={{ overflowX: 'hidden', marginTop: 10 }}>
        <ManualTableComponent data={manualMessageList ?? []} isFetching={isFetching} />
      </Stack>
    </TableLayout>
  );
};
export default ManualTable;

const SecondFilterDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
const SecondFilterInnerDiv = styled.div<{ width?: number }>`
  height: 32px;
  display: flex;
  align-items: center;
  ${({ width }) => (width ? `width: ${width}px;` : 'width: 330px;')}

  border-radius: 6px;
  border: 1px solid var(--RG06, #d8ddea);
  background: var(--RG00, #fff);
`;
const SecondFilterInnerTextDiv = styled.div`
  display: flex;
  padding: 5px 5px 5px 7px;
  align-items: center;
  gap: 12px;
  width: 100px;

  color: var(--RG02, #25282f);
  font-feature-settings: 'liga' off, 'clig' off;

  /* body14M */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 171.429% */
  letter-spacing: -0.28px;
`;
const SecondFilterInnerInputDiv = styled.div<{ isActive: boolean; hasError: boolean }>`
  display: flex;
  /* padding: 6px 0px 6px 12px; */
  align-items: center;
  gap: 4px;
  flex: 1 0 0;

  width: 100%;
  height: 30px;
  border-radius: 0px 6px 6px 0px;

  background: ${({ theme, hasError, isActive }) => theme.colors[hasError ? 'RC04_1' : isActive ? 'RG05-1' : 'RG00']};

  :hover {
    background: ${({ theme }) => theme.colors.RC03_1};
    /* 
    & > ${CalendarIconContainer} {
      background: none !important;
    } */
  }

  ${({ theme }) => theme.fontStyle.caption2}
  & > input {
    width: 100px;
    padding: 6px 10px;

    background: none;
    text-align: center;
    color: ${({ theme, isActive }) => theme.colors[isActive ? 'RG03' : 'RG02']};
  }

  :hover {
    background: ${({ theme }) => theme.colors.RC03_1};

    & > ${CalendarIconContainer} {
      background: none !important;
    }
  }

  color: #25282f;

  font-feature-settings: 'liga' off, 'clig' off;
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; /* 171.429% */
  letter-spacing: -0.28px;
`;

const CalanderIconContainer = styled.div<{ isActive: boolean; hasError: boolean }>`
  width: 16;
  height: 16;
  position: relative;
  top: 3px;
  :hover {
    background: ${({ theme }) => theme.colors.RC03_1};

    & > ${CalendarIconContainer} {
      background: none !important;
    }
  }
`;
export const SearchbarInputContainer = styled(Stack)<{ isFilled: boolean }>`
  padding: 6px 16px 6px 10px;

  & > input {
    width: 100%;
    height: 20%;
    background: transparent;

    ::placeholder {
      color: ${({ theme }) => theme.colors.RG04};
      font-size: 13px;
    }
  }

  /* & > svg {
      cursor: pointer;
    } */

  & > .removeBtn {
    display: none;
    cursor: pointer;
    margin-right: -8px;
  }

  & > .spyglass > path {
    stroke: ${({ theme }) => theme.colors.RG04};
  }

  :focus-within {
    & > .spyglass {
      width: 0;
      transition: all 0.4s;
    }

    & > .removeBtn {
      display: block;
    }
  }

  ${({ isFilled }) =>
    isFilled && {
      '& > .spyglass': {
        width: 0,
        transition: 'all 0.4s',
      },
    }}
`;
